import {createFeature, createReducer, on} from "@ngrx/store";
import {APP_CONSTANTS} from "../../app.constants";
import {AssetTypeLockStatusEnum} from "../../enums";
import {AssetTypeModel, FilterInterface, PaginationInfoInterface} from "../../interfaces";
import * as AssetTypeActions from "../actions/asset-types.actions";

export const assetTypesFeatureKey = "AssetTypes";

export interface State {
  assetTypeItems: Array<AssetTypeModel>;
  assetTypeItemsPaginated: Array<AssetTypeModel>;
  assetTypeQueryItems: Array<AssetTypeModel>;
  assetTypeQuery: string;
  assetTypeFilters: FilterInterface;
  assetTypePaginationInfo: PaginationInfoInterface;
}

export const initialState: State = {
  assetTypeItems: [],
  assetTypeItemsPaginated: [],
  assetTypeQueryItems: [
    {
      assetType: "Test 1",
      translations: [],
      lockStatus: AssetTypeLockStatusEnum.Unlocked,
      isCustomAssetType: false,
      mainAssetCount: 0,
      materialnoCount: 0,
      subAssetCount: 0,
      assetCount: 0
    },
    {
      assetType: "Test 2",
      translations: [],
      lockStatus: AssetTypeLockStatusEnum.Unlocked,
      isCustomAssetType: false,
      mainAssetCount: 0,
      materialnoCount: 0,
      subAssetCount: 0,
      assetCount: 0
    },
    {
      assetType: "Test 3",
      translations: [],
      lockStatus: AssetTypeLockStatusEnum.Unlocked,
      isCustomAssetType: false,
      mainAssetCount: 0,
      materialnoCount: 0,
      subAssetCount: 0,
      assetCount: 0
    }
  ],
  assetTypeQuery: "180",
  assetTypeFilters: {
    limit: APP_CONSTANTS.PAGINATION_PAGE_SIZE,
    offset: 0
  },
  assetTypePaginationInfo: {
    from: 0,
    to: 0,
    totalResults: 0,
    currentPage: 0,
    totalPages: 1
  }
};

export const assetTypesFeature = createFeature({
  name: assetTypesFeatureKey,
  reducer: createReducer(
    initialState,
    on(
      AssetTypeActions.addAssetType,
      (state: State, {assetType}): State => ({
        ...state,
        assetTypeItems: [...state.assetTypeItems, assetType]
      })
    ),
    on(
      AssetTypeActions.addAssetTypes,
      (state: State, {assetTypes}): State => ({
        ...state,
        assetTypeItems: [...state.assetTypeItems, ...assetTypes]
      })
    ),
    on(AssetTypeActions.updateAssetType, (state: State, {assetType}): State => {
      if (state.assetTypeItems.filter(x => x.assetType === assetType.assetType).length === 0) {
        return {...state, assetTypeItems: [...state.assetTypeItems, assetType]};
      }

      const assetTypes = state.assetTypeItems.map(item => (item.assetType === assetType.assetType ? assetType : item));
      return {
        ...state,
        assetTypeItems: assetTypes
      };
    }),
    on(AssetTypeActions.setAssetTypes, (state: State, {assetTypes}): State => {
      return {
        ...state,
        assetTypeItems: assetTypes
      };
    }),
    on(
      AssetTypeActions.setPaginatedAssetTypes,
      (state: State, {assetTypes}): State => ({
        ...state,
        assetTypeItemsPaginated: assetTypes
      })
    ),
    on(
      AssetTypeActions.setAssetTypesPaginationInfo,
      (state: State, {paginationInfo}): State => ({...state, assetTypePaginationInfo: paginationInfo})
    ),
    on(
      AssetTypeActions.gotoAssetTypesPage,
      (state: State, {page}): State => ({
        ...state,
        assetTypeFilters: {...state.assetTypeFilters, offset: state.assetTypeFilters.limit * (page - 1)},
        assetTypePaginationInfo: {...state.assetTypePaginationInfo, currentPage: page}
      })
    ),
    on(AssetTypeActions.applyAssetTypesFilters, (state: State, {filters}): State => {
      const newFilters = {...state.assetTypeFilters};
      filters.forEach(({key, value}) => {
        newFilters[key as string] = value;
      });
      newFilters.offset = 0;
      return {...state, assetTypeFilters: newFilters};
    }),
    on(
      AssetTypeActions.setAssetTypeQuery,
      (state: State, {query}): State => ({
        ...state,
        assetTypeQuery: query
      })
    ),
    on(
      AssetTypeActions.setAssetTypeQueryItems,
      (state: State, {items}): State => ({
        ...state,
        assetTypeQueryItems: items
      })
    ),
    on(
      AssetTypeActions.resetAssetTypesFilters,
      (state: State): State => ({...state, assetTypeFilters: initialState.assetTypeFilters})
    )
  )
});

export const {
  name,
  reducer,
  selectAssetTypesState,
  selectAssetTypeItems,
  selectAssetTypeItemsPaginated,
  selectAssetTypeFilters,
  selectAssetTypePaginationInfo,
  selectAssetTypeQuery,
  selectAssetTypeQueryItems
} = assetTypesFeature;
